/**
 * Currency utility functions for the application
 */

import { getCurrentCompanyCurrency } from './auth';

/**
 * Get the current user's company currency
 * @returns {string} Currency code (defaults to USD if not set)
 */
export const getCompanyCurrency = () => {
  const currency = getCurrentCompanyCurrency();
  console.log('💰 getCompanyCurrency - retrieved currency:', currency);
  // Default to USD if no currency is configured
  return currency || 'USD';
};

/**
 * Format amount with or without currency symbol based on company settings
 * @param {number} amount - Amount to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted amount
 */
export const formatAmount = (amount, options = {}) => {
  const {
    showCurrency = true,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    locale = 'en-US'
  } = options;

  if (amount === null || amount === undefined || isNaN(amount)) {
    return showCurrency ? '0.00' : '0.00';
  }

  const currency = getCompanyCurrency();
  
  // If no currency is set, format as plain number
  if (!currency || !showCurrency) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits,
      maximumFractionDigits
    }).format(amount);
  }

  // Format with currency
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits,
      maximumFractionDigits
    }).format(amount);
  } catch (error) {
    console.warn('Invalid currency code:', currency, 'falling back to plain number');
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits,
      maximumFractionDigits
    }).format(amount);
  }
};

/**
 * Format currency for display in tables and lists
 * @param {number} amount - Amount to format
 * @returns {string} Formatted amount
 */
export const formatCurrency = (amount) => {
  return formatAmount(amount, { showCurrency: true });
};

/**
 * Format amount without currency symbol
 * @param {number} amount - Amount to format
 * @returns {string} Formatted amount without currency
 */
export const formatNumber = (amount) => {
  return formatAmount(amount, { showCurrency: false });
};

/**
 * Get currency symbol if available
 * @returns {string} Currency symbol or empty string
 */
export const getCurrencySymbol = () => {
  const currency = getCompanyCurrency();
  if (!currency) return '';

  try {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    
    // Extract just the symbol
    const parts = formatter.formatToParts(0);
    const symbolPart = parts.find(part => part.type === 'currency');
    return symbolPart ? symbolPart.value : '';
  } catch (error) {
    console.warn('Error getting currency symbol for:', currency);
    return '';
  }
};

/**
 * Check if company has currency configured
 * @returns {boolean} True if currency is configured
 */
export const hasCurrency = () => {
  return !!getCompanyCurrency();
};

/**
 * Set company currency (when fetched from API)
 * @param {string} currency - Currency code
 */
export const setCompanyCurrency = (currency) => {
  try {
    const { updateCurrentUser } = require('./auth');
    updateCurrentUser({ currency });
  } catch (error) {
    console.error('Error setting company currency:', error);
  }
};